import serverIp from "./serverConfig";
import paymentConfig from "./paymentVariables";
import {
  initPostHog,
  posthogCapture,
  posthogIdentify,
  posthogReset,
  posthogSetContext
} from "./posthog";
import {
  getAnalyticsExemptionState,
  onAnalyticsExemptionSettled
} from "./analyticsExemption";
import { isSensitiveAnalyticsProperty } from "./analyticsPrivacy";

const DEFAULT_GA4_MEASUREMENT_ID = "G-ZP0GWZ33N5";
const DEFAULT_GOOGLE_ADS_ID = "AW-16890392694";
const DEFAULT_PLUSMAX_ADS_CONVERSION = "AW-16890392694/4xJRCJr316EaEPbg-_U-";
const EVENT_ENDPOINT = `${serverIp}/api/eventFlow`;
const CLIENT_ID_KEY = "cd_ga_client_id_v2";
const SESSION_KEY = "cd_ga_session_v2";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const MAX_BATCH_SIZE = 20;
const MAX_QUEUE_SIZE = 100;
const FLUSH_DELAY_MS = 700;
const PROXY_RETRY_MS = 60 * 1000;

const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const PHONE_PATTERN = /(?:\+?\d[\d\s\-().]{8,}\d)/g;
const CARD_PATTERN = /\b(?:\d[ -]*?){13,19}\b/g;
const TOKEN_PATTERN = /\b(?:eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+)\b/g;
const SENSITIVE_KEY_PATTERN = /(^|_)(email|phone|first_name|last_name|full_name|buyer_name|seller_name|owner_name|contact_name|address|password|token|card|cvv|cvc|ssn|social|iban|routing|account)(_|$)/i;

const LOW_VALUE_EVENTS = new Set([
  "api_request_completed",
  "app_initialized",
  "app_route_change",
  "app_visibility_changed",
  "engagement_heartbeat",
  "language_context_updated",
  "page_timing",
  "results_count_changed",
  "scroll_depth_reached",
  "session_started",
  "session_validated",
  "ui_click",
  "viewport_changed",
  "window_blur",
  "window_focus"
]);
const HIGH_PRIORITY_EVENTS = new Set([
  "add_payment_info",
  "begin_checkout",
  "generate_lead",
  "login",
  "purchase",
  "sign_up"
]);

let initialized = false;
let lifecycleBound = false;
let flushTimer = null;
let flushInProgress = false;
let transportMode = "proxy";
let proxyRetryAt = 0;
let currentUserId = "";
let currentUserProperties = {};
let lastEngagementAt = Date.now();
let eventCounter = 0;

const eventQueue = [];
const recentEvents = new Map();
const sessionEvents = new Set();

const hasWindow = () => typeof window !== "undefined" && typeof document !== "undefined";

const analyticsHostAllowed = () => {
  if (!hasWindow()) return false;
  const host = String(window.location.hostname || "").toLowerCase();
  if (process.env.REACT_APP_ENABLE_ANALYTICS_IN_DEV === "true") return true;
  const configuredHosts = String(
    process.env.REACT_APP_ANALYTICS_HOSTS || "casadata.io,www.casadata.io"
  )
    .split(",")
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  return configuredHosts.includes(host);
};

const userOptedOut = () => {
  if (!hasWindow()) return false;

  // Browser privacy signals must still win when storage is unavailable (for
  // example Safari privacy mode or a hardened storage policy).
  try {
    if (navigator?.globalPrivacyControl === true) return true;
  } catch {}
  try {
    const dnt = String(
      navigator?.doNotTrack || window?.doNotTrack || navigator?.msDoNotTrack || ""
    ).toLowerCase();
    if (dnt === "1" || dnt === "yes") return true;
  } catch {}

  try {
    const local = String(localStorage.getItem("cd_analytics_opt_out") || "").toLowerCase();
    const session = String(sessionStorage.getItem("cd_analytics_opt_out") || "").toLowerCase();
    return ["true", "1"].includes(local) || ["true", "1"].includes(session);
  } catch {
    return false;
  }
};

const shouldTrack = () => (
  hasWindow() &&
  !userOptedOut() &&
  analyticsHostAllowed()
);

const sanitizeString = (value, limit = 100) => (
  String(value || "")
    .replace(EMAIL_PATTERN, "[redacted]")
    .replace(PHONE_PATTERN, "[redacted]")
    .replace(CARD_PATTERN, "[redacted]")
    .replace(TOKEN_PATTERN, "[redacted]")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, limit)
);

const sanitizeValue = (value) => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "string") return sanitizeString(value);
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value === "boolean") return value;
  return undefined;
};

const sanitizeItem = (item) => {
  if (!item || typeof item !== "object") return null;
  const allowed = new Set([
    "item_id",
    "item_name",
    "item_brand",
    "item_category",
    "item_variant",
    "price",
    "quantity"
  ]);
  const clean = {};
  Object.entries(item).forEach(([rawKey, value]) => {
    const key = String(rawKey || "").trim().toLowerCase();
    if (!allowed.has(key)) return;
    const sanitized = sanitizeValue(value);
    if (sanitized !== undefined && sanitized !== "") clean[key] = sanitized;
  });
  if (!clean.item_id && !clean.item_name) return null;
  return clean;
};

const sanitizeParams = (params = {}) => {
  const out = {};
  Object.entries(params || {}).slice(0, 25).forEach(([rawKey, value]) => {
    const key = String(rawKey || "").trim().slice(0, 40);
    if (!key || key.startsWith("_") || SENSITIVE_KEY_PATTERN.test(key) || isSensitiveAnalyticsProperty(key)) return;
    if (key === "items" && Array.isArray(value)) {
      const items = value.map(sanitizeItem).filter(Boolean).slice(0, 10);
      if (items.length) out.items = items;
      return;
    }
    const sanitized = sanitizeValue(value);
    if (sanitized !== undefined && sanitized !== "") out[key] = sanitized;
  });
  return out;
};

const normalizeEventName = (eventName) => {
  let name = String(eventName || "")
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (!name) name = "custom_event";
  if (!/^[a-z]/.test(name)) name = `e_${name}`;
  return name.slice(0, 40);
};

const AI_SOURCE_PATTERNS = [
  ["chatgpt", "chatgpt"],
  ["openai", "chatgpt"],
  ["perplexity", "perplexity"],
  ["claude", "claude"],
  ["anthropic", "claude"],
  ["gemini", "gemini"],
  ["copilot", "copilot"]
];
const SEARCH_SOURCE_PATTERNS = [
  ["google", "google"],
  ["bing", "bing"],
  ["duckduckgo", "duckduckgo"],
  ["yahoo", "yahoo"],
  ["ecosia", "ecosia"],
  ["yandex", "yandex"],
  ["baidu", "baidu"]
];

const matchDiscoverySource = (value, patterns) => {
  const normalized = String(value || "").toLowerCase();
  const match = patterns.find(([needle]) => normalized.includes(needle));
  return match ? match[1] : "";
};

export const getDiscoveryContext = (locationHref = "", referrer = "") => {
  try {
    const fallbackOrigin = hasWindow() ? window.location.origin : "https://casadata.io";
    const current = new URL(locationHref || (hasWindow() ? window.location.href : fallbackOrigin), fallbackOrigin);
    let referrerHost = "";
    try {
      referrerHost = referrer ? new URL(referrer, fallbackOrigin).hostname.toLowerCase() : "";
    } catch {}
    const utmSource = String(current.searchParams.get("utm_source") || "").toLowerCase();
    const utmMedium = String(current.searchParams.get("utm_medium") || "").toLowerCase();
    const paidClick = ["gclid", "gbraid", "wbraid", "dclid", "msclkid", "fbclid", "ttclid"]
      .some((key) => Boolean(current.searchParams.get(key)));
    const sourceInput = utmSource || referrerHost;
    const aiSource = matchDiscoverySource(sourceInput, AI_SOURCE_PATTERNS);
    const searchSource = matchDiscoverySource(sourceInput, SEARCH_SOURCE_PATTERNS);
    const paidMedium = /(cpc|ppc|paid|display|affiliate|retarget)/.test(utmMedium);

    let discoveryChannel = "direct";
    let discoverySource = "direct";
    if (paidClick || paidMedium) {
      discoveryChannel = "paid";
      discoverySource = sanitizeString(utmSource || searchSource || referrerHost || "paid", 60);
    } else if (aiSource) {
      discoveryChannel = "ai_assistant";
      discoverySource = aiSource;
    } else if (searchSource) {
      discoveryChannel = "organic_search";
      discoverySource = searchSource;
    } else if (utmSource) {
      discoveryChannel = utmMedium || "campaign";
      discoverySource = sanitizeString(utmSource, 60);
    } else if (referrerHost) {
      discoveryChannel = "referral";
      discoverySource = sanitizeString(referrerHost, 80);
    }

    return {
      discovery_channel: discoveryChannel,
      discovery_source: discoverySource,
      ...(referrerHost ? { referrer_host: sanitizeString(referrerHost, 80) } : {})
    };
  } catch {
    return { discovery_channel: "direct", discovery_source: "direct" };
  }
};

const safePageLocation = () => {
  if (!hasWindow()) return "";
  try {
    const source = new URL(window.location.href);
    const safe = new URL(`${source.origin}${source.pathname}`);
    [
      "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
      "gclid", "gbraid", "wbraid", "dclid", "msclkid", "fbclid", "ttclid", "srsltid"
    ].forEach((key) => {
      const value = sanitizeString(source.searchParams.get(key) || "", 80);
      if (value) safe.searchParams.set(key, value);
    });
    return safe.toString().slice(0, 300);
  } catch {
    return String(window.location.pathname || "/").slice(0, 100);
  }
};

const getClientId = () => {
  if (!hasWindow()) return "";
  try {
    const cookieMatch = document.cookie.match(/(?:^|;\s*)_ga=GA\d+\.\d+\.(\d+\.\d+)/);
    if (cookieMatch?.[1]) {
      localStorage.setItem(CLIENT_ID_KEY, cookieMatch[1]);
      return cookieMatch[1];
    }
    const stored = String(localStorage.getItem(CLIENT_ID_KEY) || "");
    if (/^\d+\.\d+$/.test(stored)) return stored;
    const created = `${Math.floor(Math.random() * 2147483647)}.${Math.floor(Date.now() / 1000)}`;
    localStorage.setItem(CLIENT_ID_KEY, created);
    return created;
  } catch {
    return `${Math.floor(Math.random() * 2147483647)}.${Math.floor(Date.now() / 1000)}`;
  }
};

// GA4's own session id lives in the _ga_<stream> cookie (GS1 "GS1.1.<ts>." or
// GS2 "GS2.1.s<ts>$" formats). Reusing it keeps proxy-relayed events in the
// same GA4 session as gtag-collected hits, so funnels don't split.
const readCookieSessionId = () => {
  if (!hasWindow()) return 0;
  try {
    const ga4Id = String(process.env.REACT_APP_GA4_MEASUREMENT_ID || DEFAULT_GA4_MEASUREMENT_ID).trim();
    const suffix = ga4Id.replace(/^G-/i, "");
    if (!suffix) return 0;
    const escaped = suffix.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)_ga_${escaped}=GS\\d+\\.\\d+\\.s?(\\d{9,11})`));
    const id = match ? Number(match[1]) : 0;
    return Number.isFinite(id) && id > 0 ? id : 0;
  } catch {
    return 0;
  }
};

const getSessionId = () => {
  const now = Date.now();
  if (!hasWindow()) return Math.floor(now / 1000);
  let stored = null;
  try { stored = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null"); } catch {}
  const persist = (id) => {
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, lastActivity: now })); } catch {}
  };
  const cookieSession = readCookieSessionId();
  if (cookieSession > 0) {
    if (Number(stored?.id) !== cookieSession) sessionEvents.clear();
    persist(cookieSession);
    return cookieSession;
  }
  if (stored?.id && now - Number(stored.lastActivity || 0) < SESSION_TIMEOUT_MS) {
    persist(Number(stored.id));
    return Number(stored.id);
  }
  const id = Math.floor(now / 1000);
  persist(id);
  sessionEvents.clear();
  return id;
};

const getDevice = () => {
  if (!hasWindow()) return {};
  const width = Number(window.innerWidth || window.screen?.width || 0);
  const height = Number(window.innerHeight || window.screen?.height || 0);
  return {
    category: width && width < 700 ? "mobile" : (width && width < 1100 ? "tablet" : "desktop"),
    language: sanitizeString(navigator.language || "", 20),
    screen_resolution: width && height ? `${Math.round(width)}x${Math.round(height)}` : undefined,
    operating_system: sanitizeString(navigator.platform || "", 30)
  };
};

const ensureGtag = () => {
  if (!hasWindow()) return;
  if (!Array.isArray(window.dataLayer)) window.dataLayer = [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
  }
};

const safeGtag = (...args) => {
  if (!shouldTrack()) return;
  ensureGtag();
  try { window.gtag(...args); } catch {}
};

// GA4's Measurement Protocol cannot populate traffic-source attribution
// (source / medium / campaign / gclid), so page_view hits must reach GA4
// through gtag whenever the tag can load. Everything else keeps flowing
// through the first-party relay, joined to the same session via
// client_id + session_id. Page views buffer until the tag settles and fall
// back to the relay when it is blocked, so ad-blocked users stay counted.
const GTAG_SETTLE_TIMEOUT_MS = 8000;
const MAX_PENDING_PAGE_VIEWS = 20;
let gtagScriptState = "pending"; // "pending" | "ready" | "failed"
const pendingPageViews = [];
let initialDiscoveryContext = null;
let lastLogicalPageLocation = "";

const queueForProxy = (name, params, priority = false) => {
  if (eventQueue.length >= MAX_QUEUE_SIZE) eventQueue.shift();
  eventQueue.push({ name, params });
  // Preserve the intended analytics bootstrap delay. Early events remain in
  // memory until gtag has established its cookie identity (or definitively
  // failed), preventing SDK work in the LCP window and split cold sessions.
  if (initialized) scheduleFlush(priority ? 0 : FLUSH_DELAY_MS);
};

const routePageView = (name, params) => {
  if (gtagScriptState === "ready") {
    const { session_id, engagement_time_msec, ...gtagParams } = params;
    safeGtag("event", name, gtagParams);
    return;
  }
  if (gtagScriptState === "failed") {
    queueForProxy(name, params);
    return;
  }
  if (pendingPageViews.length >= MAX_PENDING_PAGE_VIEWS) pendingPageViews.shift();
  pendingPageViews.push({ name, params });
};

const settleGtagScript = (state) => {
  if (gtagScriptState === "ready" || state === gtagScriptState) return;
  gtagScriptState = state;
  pendingPageViews.splice(0, pendingPageViews.length).forEach((event) => {
    routePageView(event.name, event.params);
  });
  if (eventQueue.length) scheduleFlush(0);
};

const sendDirect = (events) => {
  events.forEach((event) => safeGtag("event", event.name, event.params));
};

const makePayload = (events) => {
  const payload = { client_id: getClientId(), events, device: getDevice() };
  if (currentUserId) payload.user_id = currentUserId;
  if (Object.keys(currentUserProperties).length) payload.user_properties = currentUserProperties;
  return payload;
};

const scheduleFlush = (delay = FLUSH_DELAY_MS) => {
  if (flushTimer || !eventQueue.length) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    flushQueue();
  }, delay);
};

const flushQueue = async () => {
  if (!shouldTrack() || flushInProgress || !eventQueue.length) return;
  flushInProgress = true;
  if (flushTimer) {
    window.clearTimeout(flushTimer);
    flushTimer = null;
  }
  const batch = eventQueue.splice(0, MAX_BATCH_SIZE);
  try {
    if (transportMode === "gtag" && Date.now() < proxyRetryAt) {
      sendDirect(batch);
      return;
    }
    const response = await fetch(EVENT_ENDPOINT, {
      method: "POST",
      mode: "cors",
      cache: "no-store",
      credentials: "omit",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(makePayload(batch))
    });
    if (!response.ok) throw new Error(`ga_proxy_${response.status}`);
    transportMode = "proxy";
    proxyRetryAt = 0;
  } catch {
    sendDirect(batch);
    transportMode = "gtag";
    proxyRetryAt = Date.now() + PROXY_RETRY_MS;
  } finally {
    flushInProgress = false;
    if (eventQueue.length) scheduleFlush(0);
  }
};

const flushOnPageHide = () => {
  if (!shouldTrack()) return;
  if (gtagScriptState === "pending" && pendingPageViews.length) {
    pendingPageViews.splice(0, pendingPageViews.length).forEach((event) => {
      if (eventQueue.length >= MAX_QUEUE_SIZE) eventQueue.shift();
      eventQueue.push({ name: event.name, params: event.params });
    });
  }
  if (!eventQueue.length) return;
  if (flushTimer) {
    window.clearTimeout(flushTimer);
    flushTimer = null;
  }
  const batch = eventQueue.splice(0, MAX_BATCH_SIZE);
  if (transportMode === "gtag" && Date.now() < proxyRetryAt) {
    sendDirect(batch);
    return;
  }
  try {
    const body = new Blob([JSON.stringify(makePayload(batch))], { type: "application/json" });
    if (navigator.sendBeacon(EVENT_ENDPOINT, body)) return;
  } catch {}
  sendDirect(batch);
};

const bindLifecycle = () => {
  if (!hasWindow() || lifecycleBound) return;
  lifecycleBound = true;
  window.addEventListener("pagehide", flushOnPageHide);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushOnPageHide();
  });
};

const eventSignature = (name, params) => {
  if (name === "page_view") return `${name}|${params.app_route || params.page_path || "unknown"}`;
  if (name === "purchase" && params.transaction_id) return `${name}|${params.transaction_id}`;
  if (name === "funnel_stage_reached") return `${name}|${params.stage || "unknown"}`;
  if (name === "search") return `${name}|${params.search_type || params.search_term || "search"}`;
  const stable = { ...params };
  delete stable.engagement_time_msec;
  delete stable.event_id;
  delete stable.session_id;
  return `${name}|${JSON.stringify(Object.keys(stable).sort().reduce((out, key) => {
    out[key] = stable[key];
    return out;
  }, {}))}`;
};

const isDuplicate = (name, params) => {
  const signature = eventSignature(name, params);
  if ((name === "funnel_stage_reached" || name === "purchase") && sessionEvents.has(signature)) return true;
  const now = Date.now();
  const previous = recentEvents.get(signature) || 0;
  if (now - previous <= (name === "page_view" ? 2500 : 1800)) return true;
  recentEvents.set(signature, now);
  if (name === "funnel_stage_reached" || name === "purchase") sessionEvents.add(signature);
  recentEvents.forEach((timestamp, key) => {
    if (now - timestamp > 60000) recentEvents.delete(key);
  });
  return false;
};

export const flushAnalytics = () => flushOnPageHide();

export const getAnalyticsContext = () => {
  if (!shouldTrack()) return {};
  return {
    client_id: getClientId(),
    session_id: getSessionId()
  };
};

export const initializePostHogAnalytics = () => {
  if (!hasWindow() || !shouldTrack()) return false;
  const exemptionState = getAnalyticsExemptionState();
  if (exemptionState === "pending") {
    onAnalyticsExemptionSettled(() => initializePostHogAnalytics());
    return false;
  }
  if (exemptionState !== "clear") return false;
  initPostHog();
  return true;
};

export const initializeAnalytics = () => {
  if (!hasWindow() || initialized || !shouldTrack()) return;
  // Hold every third-party tag until the IP-exemption check settles. Events
  // fired meanwhile buffer locally (page views) or hit the first-party relay,
  // which drops exempt IPs server-side — so nothing contaminates the numbers.
  const exemptionState = getAnalyticsExemptionState();
  if (exemptionState === "pending") {
    onAnalyticsExemptionSettled(() => initializeAnalytics());
    return;
  }
  if (exemptionState !== "clear") return;
  const ga4Id = String(process.env.REACT_APP_GA4_MEASUREMENT_ID || DEFAULT_GA4_MEASUREMENT_ID).trim();
  const adsId = String(process.env.REACT_APP_GOOGLE_ADS_ID || DEFAULT_GOOGLE_ADS_ID).trim();

  ensureGtag();
  if (!document.getElementById("cd-ga4-loader")) {
    const script = document.createElement("script");
    script.id = "cd-ga4-loader";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga4Id || adsId)}`;
    script.onload = () => settleGtagScript("ready");
    script.onerror = () => settleGtagScript("failed");
    document.head.appendChild(script);
    window.setTimeout(() => settleGtagScript("failed"), GTAG_SETTLE_TIMEOUT_MS);
  }

  safeGtag("js", new Date());
  if (ga4Id) safeGtag("config", ga4Id, { send_page_view: false, anonymize_ip: true });
  if (adsId) safeGtag("config", adsId);
  bindLifecycle();
  // PostHog rides the same shouldTrack() gate: only initialized when analytics
  // is permitted for this visitor/host.
  initializePostHogAnalytics();
  initialized = true;
};

export const trackEvent = (eventName, params = {}) => {
  if (!shouldTrack()) return false;
  const name = normalizeEventName(eventName);
  if (LOW_VALUE_EVENTS.has(name) || name.startsWith("db_")) return false;

  const now = Date.now();
  const engagementTime = Math.max(1, Math.min(60000, now - lastEngagementAt));
  lastEngagementAt = now;
  eventCounter += 1;
  const safeParams = sanitizeParams({
    ...params,
    session_id: getSessionId(),
    engagement_time_msec: engagementTime,
    event_id: `cd_${now.toString(36)}_${eventCounter.toString(36)}`
  });

  if (isDuplicate(name, safeParams)) return false;
  // Forward every meaningful, PII-scrubbed event to PostHog with the exact same
  // params GA4 receives — one instrumentation, two destinations.
  posthogCapture(name, safeParams);
  if (name === "page_view") {
    routePageView(name, safeParams);
    return true;
  }
  queueForProxy(name, safeParams, HIGH_PRIORITY_EVENTS.has(name));
  return true;
};

const externalReferrer = () => {
  if (!hasWindow()) return "";
  try {
    const referrer = String(document.referrer || "");
    if (!referrer) return "";
    const referrerUrl = new URL(referrer);
    if (referrerUrl.origin === window.location.origin) return "";
    return `${referrerUrl.origin}${referrerUrl.pathname}`.slice(0, 200);
  } catch {
    return "";
  }
};

export const trackPageView = (pageTitle, params = {}) => {
  if (!hasWindow()) return false;
  const fallbackRoute = String(pageTitle || "casadata")
    .replace(/\b(casadata|page|premium|free)\b/gi, " ");
  const route = normalizeEventName(
    params.app_route || params.frontpage_route || params.route || fallbackRoute
  );
  const currentLocation = safePageLocation();
  const originalExternalReferrer = externalReferrer();
  if (!initialDiscoveryContext) {
    initialDiscoveryContext = getDiscoveryContext(
      window.location.href,
      document.referrer || ""
    );
  }
  const logicalReferrer = lastLogicalPageLocation && lastLogicalPageLocation !== currentLocation
    ? lastLogicalPageLocation
    : (!lastLogicalPageLocation ? originalExternalReferrer : "");
  const tracked = trackEvent("page_view", {
    page_title: pageTitle || "CASADATA",
    page_location: currentLocation,
    page_path: String(window.location.pathname || "/").slice(0, 100),
    page_referrer: logicalReferrer || undefined,
    ...initialDiscoveryContext,
    ...params,
    app_route: route
  });
  if (tracked) lastLogicalPageLocation = currentLocation;
  return tracked;
};

// send_to must bypass the PII scrubber: the phone-number pattern redacts the
// 11-digit Ads account id ("AW-16890392694" -> "AW-[redacted]"), which breaks
// the conversion upload. Validate its shape instead.
const ADS_SEND_TO_PATTERN = /^AW-\d+(\/[A-Za-z0-9_-]+)?$/;

export const trackAdsConversion = (sendTo = DEFAULT_PLUSMAX_ADS_CONVERSION, params = {}) => {
  initializeAnalytics();
  const target = ADS_SEND_TO_PATTERN.test(String(sendTo || "").trim())
    ? String(sendTo).trim()
    : DEFAULT_PLUSMAX_ADS_CONVERSION;
  const transactionId = String(params.transaction_id || "").trim().slice(0, 100);
  const payload = sanitizeParams(params);
  payload.send_to = target;
  if (transactionId) payload.transaction_id = transactionId;
  safeGtag("event", "conversion", payload);
};

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
};

// Pre-tax display prices per plan/cycle so funnel events always carry a value
// even when the call site doesn't know the amount. Purchase events still
// prefer the server-reported charge when available.
const PLAN_PRICE_TABLE = {
  plus: { monthly: paymentConfig.plusPrice },
  plusmax: { monthly: paymentConfig.plusMaxMonthly, annual: paymentConfig.plusMaxYearly },
  plusmaxpro: { monthly: paymentConfig.plusMaxProMonthly, annual: paymentConfig.plusMaxProYearly },
  pass72: { one_time: paymentConfig.pass72Price }
};

export const planPrice = (plan, billing) => {
  const planKey = String(plan || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
  const tiers = PLAN_PRICE_TABLE[planKey];
  if (!tiers) return undefined;
  const billingKey = String(billing || "").toLowerCase().replace(/[^a-z0-9_]+/g, "");
  if (Object.prototype.hasOwnProperty.call(tiers, billingKey)) return tiers[billingKey];
  if (billingKey === "yearly") return tiers.annual;
  if (tiers.monthly !== undefined) return tiers.monthly;
  const values = Object.values(tiers);
  return values.length === 1 ? values[0] : undefined;
};

const membershipItem = (plan, billing, value) => {
  const item = {
    item_id: normalizeEventName(`${plan || "membership"}_${billing || "standard"}`),
    item_name: plan || "CASADATA Membership",
    item_brand: "CASADATA",
    item_category: "membership",
    item_variant: billing || "standard",
    quantity: 1
  };
  const price = toFiniteNumber(value);
  if (price !== undefined) item.price = price;
  return item;
};

export const trackViewItem = ({ plan = "PlusMAX", billing = "monthly", value, currency = "USD", source = "" } = {}) => {
  const price = toFiniteNumber(value) ?? planPrice(plan, billing);
  return trackEvent("view_item", {
    plan_tier: plan,
    billing_cycle: billing,
    value: price,
    currency,
    source,
    items: [membershipItem(plan, billing, price)]
  });
};

export const trackViewItemList = ({ listId = "pricing_page", listName = "Pricing", source = "", plans } = {}) => {
  const entries = Array.isArray(plans) && plans.length ? plans : [
    { plan: "PlusMAX", billing: "monthly" },
    { plan: "PlusMAX", billing: "annual" },
    { plan: "PlusMAXPro", billing: "monthly" },
    { plan: "Pass72", billing: "one_time" }
  ];
  return trackEvent("view_item_list", {
    item_list_id: listId,
    item_list_name: listName,
    source,
    items: entries.map((entry) => membershipItem(
      entry.plan,
      entry.billing,
      toFiniteNumber(entry.value) ?? planPrice(entry.plan, entry.billing)
    ))
  });
};

export const trackSelectItem = ({ plan = "PlusMAX", billing = "monthly", value, currency = "USD", source = "", listId = "" } = {}) => {
  const price = toFiniteNumber(value) ?? planPrice(plan, billing);
  return trackEvent("select_item", {
    plan_tier: plan,
    billing_cycle: billing,
    value: price,
    currency,
    source,
    item_list_id: listId || undefined,
    items: [membershipItem(plan, billing, price)]
  });
};

export const trackBeginCheckout = ({ plan = "PlusMAX", billing = "monthly", value, currency = "USD", source = "" } = {}) => {
  const price = toFiniteNumber(value) ?? planPrice(plan, billing);
  return trackEvent("begin_checkout", {
    plan_tier: plan,
    billing_cycle: billing,
    value: price,
    currency,
    source,
    items: [membershipItem(plan, billing, price)]
  });
};

export const trackAddPaymentInfo = ({ plan = "PlusMAX", billing = "", method = "", value, currency = "USD" } = {}) => {
  const price = toFiniteNumber(value) ?? planPrice(plan, billing);
  return trackEvent("add_payment_info", {
    plan_tier: plan,
    billing_cycle: billing,
    payment_method: method,
    payment_type: method,
    value: price,
    currency,
    items: [membershipItem(plan, billing, price)]
  });
};

export const trackPurchase = ({ transactionId = "", plan = "PlusMAX", billing = "", method = "", value, tax, currency = "USD", coupon = "" } = {}) => {
  const paidValue = toFiniteNumber(value) ?? planPrice(plan, billing);
  const tracked = trackEvent("purchase", {
    transaction_id: transactionId,
    plan_tier: plan,
    billing_cycle: billing,
    payment_method: method,
    affiliation: method === "paypal" ? "PayPal" : (method ? "Stripe" : undefined),
    coupon: coupon || undefined,
    value: paidValue,
    tax: toFiniteNumber(tax),
    currency,
    items: [membershipItem(plan, billing, paidValue)]
  });
  // The Google Ads conversion mirrors the accepted GA4 purchase: it only
  // fires on confirmed payment (never on checkout attempts), carries the
  // paid value for value-based bidding, and dedupes on transaction_id.
  if (tracked) {
    trackAdsConversion(undefined, {
      value: paidValue,
      currency,
      transaction_id: transactionId || undefined
    });
  }
  return tracked;
};
export const trackSearchInteraction = ({ searchType = "map_polygon", resultCount, isTrialSearch = false } = {}) => {
  if (resultCount !== undefined && resultCount !== null) return false;
  return trackEvent("search", {
    search_term: searchType,
    search_type: searchType,
    is_trial_search: Boolean(isTrialSearch)
  });
};

export const setAnalyticsUser = ({ userId = "", planTier = "", isTrial = false, language = "" } = {}) => {
  const safeId = sanitizeString(userId, 64);
  currentUserId = safeId.includes("@") ? "" : safeId;
  currentUserProperties = sanitizeParams({
    plan_tier: planTier,
    trial_user: Boolean(isTrial),
    language
  });
  safeGtag("set", "user_id", currentUserId || null);
  safeGtag("set", "user_properties", currentUserProperties);
  // Tie PostHog behavior/replays/funnel to the same numeric account id.
  posthogIdentify(currentUserId, currentUserProperties);
};

export const setAnalyticsAnonymousContext = ({ planTier = "anonymous", isTrial = false, language = "" } = {}) => {
  currentUserId = "";
  currentUserProperties = sanitizeParams({
    plan_tier: planTier || "anonymous",
    trial_user: Boolean(isTrial),
    language,
    auth_state: isTrial ? "trial" : "anonymous"
  });
  safeGtag("set", "user_id", null);
  safeGtag("set", "user_properties", currentUserProperties);
  posthogSetContext(currentUserProperties);
};

export const clearAnalyticsUser = () => {
  currentUserId = "";
  currentUserProperties = { plan_tier: "anonymous", trial_user: false };
  safeGtag("set", "user_id", null);
  safeGtag("set", "user_properties", currentUserProperties);
  // Un-link the session so the next user isn't merged with this one.
  posthogReset();
};

// Retained for old imports. Legacy records are intentionally not emitted.
export const trackLegacyAnalytics = () => false;
